import type { AuthTokens } from '@/types';

const STORAGE_KEY = 'richy-client-hub-auth';
const REMEMBER_PREF_KEY = 'richy-client-hub-remember-me';
const EMAIL_KEY = 'richy-client-hub-remembered-email';

export interface StoredAuth extends AuthTokens {
  email?: string;
}

function readStorageItem(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    /* private mode / storage disabled */
  }
}

function removeStorageItem(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    /* private mode / storage disabled */
  }
}

export function loadRememberMePref(): boolean {
  return readStorageItem(localStorage, REMEMBER_PREF_KEY) === '1';
}

export function saveRememberMePref(remember: boolean): void {
  if (remember) writeStorageItem(localStorage, REMEMBER_PREF_KEY, '1');
  else removeStorageItem(localStorage, REMEMBER_PREF_KEY);
}

export function loadRememberedEmail(): string {
  return readStorageItem(localStorage, EMAIL_KEY) ?? '';
}

export function saveRememberedEmail(email: string | null): void {
  const value = String(email || '').trim();
  if (value) writeStorageItem(localStorage, EMAIL_KEY, value);
  else removeStorageItem(localStorage, EMAIL_KEY);
}

export function isPersistentAuth(): boolean {
  return Boolean(readStorageItem(localStorage, STORAGE_KEY));
}

export function loadAuth(): StoredAuth | null {
  const raw = readStorageItem(localStorage, STORAGE_KEY) ?? readStorageItem(sessionStorage, STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

export function saveAuth(auth: StoredAuth, persist = isPersistentAuth() || loadRememberMePref()): void {
  const raw = JSON.stringify(auth);
  if (persist) {
    writeStorageItem(localStorage, STORAGE_KEY, raw);
    removeStorageItem(sessionStorage, STORAGE_KEY);
    return;
  }
  writeStorageItem(sessionStorage, STORAGE_KEY, raw);
  removeStorageItem(localStorage, STORAGE_KEY);
}

export function clearAuth(): void {
  removeStorageItem(sessionStorage, STORAGE_KEY);
  removeStorageItem(localStorage, STORAGE_KEY);
}

export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const segment = token.split('.')[1];
    if (!segment) return {};
    const json = atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function getEmailFromIdToken(idToken?: string): string | undefined {
  if (!idToken) return undefined;
  const payload = decodeJwtPayload(idToken);
  const email = payload.email;
  return typeof email === 'string' ? email : undefined;
}

/** True when the access token still has more than `skewSeconds` left. */
export function isAccessTokenFresh(token?: string, skewSeconds = 60): boolean {
  if (!token) return false;
  const exp = decodeJwtPayload(token).exp;
  if (typeof exp !== 'number') return true;
  return exp * 1000 > Date.now() + skewSeconds * 1000;
}
